import type { ManuscriptDocument } from '../../lib/pdf';
import type { PatternRunResult } from '../../lib/patterns';
import { loadGuidelineById, type VerificationStatus } from '../../lib/checklists';

interface Props {
  manuscript: ManuscriptDocument | null;
  analysis: PatternRunResult | null;
}

function verificationNote(s: VerificationStatus | undefined): {
  text: string;
  warn: boolean;
} {
  switch (s) {
    case 'verified':
      return { text: 'checklist text source-verified verbatim', warn: false };
    case 'verified_with_consolidations':
      return { text: 'source-verified (some sub-items consolidated)', warn: false };
    case 'training_data_best_effort':
      return { text: 'checklist text best-effort — NOT source-verified', warn: true };
    case 'draft':
      return { text: 'checklist encoding is draft', warn: true };
    default:
      return { text: '', warn: false };
  }
}

export function StudyTypeCard({ manuscript, analysis }: Props) {
  if (!manuscript || !analysis) {
    return (
      <section className="ms-card">
        <div className="ms-ribbon ms-ribbon--brand">
          <h2>Study type &amp; guideline</h2>
        </div>
        <p className="text-xs leading-relaxed text-slate-600">
          Classifies the study design from cues across the title, abstract and methods, then
          maps it to the matching EQUATOR reporting guideline —{' '}
          <strong>CONSORT</strong> (randomised trials), <strong>STROBE</strong> (cohort /
          case-control / cross-sectional), <strong>PRISMA</strong> (systematic reviews &amp;
          meta-analyses), <strong>STARD</strong> (diagnostic-accuracy),{' '}
          <strong>CARE</strong> (case reports). Confidence is calibrated: low confidence asks
          you to confirm, and if no formal guideline fits the design the tool says so rather
          than forcing one.
        </p>
      </section>
    );
  }

  const { classifier, methodology } = analysis;
  const confidencePct = Math.round(classifier.confidence * 100);
  const lowConfidence = classifier.confidence < 0.75 && classifier.label !== 'unclassified';
  const guideline = methodology ? loadGuidelineById(methodology.guidelineId) : undefined;
  const vnote = verificationNote(guideline?.verification_status);
  // Classified to a real design, but no EQUATOR checklist is encoded for it.
  const classifiedButUncovered =
    !methodology &&
    !classifier.noFormalGuideline &&
    classifier.label !== 'unclassified';

  return (
    <section className="ms-card">
      <div className="ms-ribbon ms-ribbon--brand">
        <h2>Study type &amp; guideline</h2>
      </div>
      <p className="mt-1 text-sm text-slate-900">
        <span className="font-medium">{classifier.label}</span>{' '}
        <span className="text-xs text-slate-500">({confidencePct}% confidence)</span>
      </p>
      {methodology && (
        <p className="mt-0.5 text-xs text-slate-600">
          Guideline: <span className="font-medium">{methodology.guidelineId}</span> v
          {methodology.guidelineVersion}
          {vnote.text && (
            <span className={vnote.warn ? 'ml-1 text-amber-700' : 'ml-1 text-slate-400'}>
              · {vnote.text}
            </span>
          )}
        </p>
      )}
      {classifier.noFormalGuideline && (
        <p className="mt-1 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">
          No formal EQUATOR guideline applies. Assess against ICMJE narrative norms.
        </p>
      )}
      {classifiedButUncovered && (
        <p className="mt-1 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">
          No EQUATOR checklist is encoded for this design. Assess against the appropriate
          reporting guideline (e.g. PRISMA-DTA, TRIPOD+AI, CHEERS, SRQR/COREQ, ARRIVE, SPIRIT)
          and journal norms.
        </p>
      )}
      {lowConfidence && (
        <p className="mt-1 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">
          Low confidence — editor, please confirm. Alternatives:{' '}
          {classifier.alternatives.map((a) => a.label).join(', ') || 'none'}.
        </p>
      )}
      <p className="mt-1 text-[11px] text-slate-500">{classifier.rationale}</p>
    </section>
  );
}
